import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface IMetricNode {
  startDate: string
  endDate: string
  metricData: Record<
    'total_sales',
    {
      name: string
      unit: 'CENT'
      value: number
    }
  >
}

type IDataType = {
  trendLocationGroupMetricValues: {
    nodes: IMetricNode[]
  }
}

const createCurrentQuery = (yearIndex: number) => gql`
  query listLocationMetricValuesV2Year${yearIndex}(
    $iStartDate: Date!
    $iEndDate: Date!
    $iLocationGroupIds: [Int!]!
    $iMetricCodes: [String!]!
  ) {
    trendLocationGroupMetricValues: listLocationMetricValuesV2(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iParams: {
        location_group_ids: $iLocationGroupIds
        group_by: ["LOCATION_GROUP", "BUSINESS_MONTH"]
        metrics: $iMetricCodes
        use_location_history: true
      }
    ) {
      nodes {
        startDate
        endDate
        metricData
      }
    }
  }
`

const createYoyQuery = (yearIndex: number) => gql`
  query listLocationMetricValuesV2YoyYear${yearIndex}(
    $iStartDate: Date!
    $iEndDate: Date!
    $iLocationGroupIds: [Int!]!
    $iMetricCodes: [String!]!
  ) {
    trendLocationGroupMetricValues: listLocationMetricValuesV2(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iParams: {
        location_group_ids: $iLocationGroupIds
        group_by: ["LOCATION_GROUP", "BUSINESS_MONTH"]
        metrics: $iMetricCodes
        use_location_history: true
        use_yoy: true
      }
    ) {
      nodes {
        startDate
        endDate
        metricData
      }
    }
  }
`

const currentQueries = Array.from({ length: 4 }, (_, i) =>
  createCurrentQuery(i),
)
const yoyQueries = Array.from({ length: 4 }, (_, i) => createYoyQuery(i))

export const bbbAllPeriodMetricConfigs = {
  period: 'string',
  '<%- JSON(date?.getCalendar("period", "year").yearIds.map(id => ({ key: "compSalesPercent" + id, label: id }))) %>':
    'percent',
} as const

const HARDCODE_DATA = {
  P1: { compSalesPercent2022: 5.66 },
  P2: { compSalesPercent2022: 15.55 },
  P3: { compSalesPercent2022: 4.62 },
  P4: { compSalesPercent2022: -4.38 },
  P5: { compSalesPercent2022: 2.42 },
  P6: { compSalesPercent2022: 2.52 },
  P7: { compSalesPercent2022: 1.97 },
  P8: { compSalesPercent2022: 11.64 },
  P9: { compSalesPercent2022: 8.27 },
  P10: { compSalesPercent2022: 7.12 },
  P11: { compSalesPercent2022: 6.48 },
  P12: { compSalesPercent2022: 3.45 },
} as Record<string, Record<string, number>>
const HARDCODE_YEAR = 2022
const MAX_YEARS_TO_SHOW = 5
const NOW = moment.utc()

const shouldSkipYear = (
  periods: { year?: number }[],
  skip: boolean,
): boolean => {
  if (skip || periods.length === 0) return true
  const year = periods[0]?.year
  return !year || year <= HARDCODE_YEAR
}

const useBbbAllPeriodMetric = () => {
  const { variables } = useVariables()
  const periodCalendar = useMemo(
    () => variables.date?.getCalendar('period')?.calendar || [[]],
    [variables.date],
  )

  const currentYear = periodCalendar[0]?.[0]?.year || new Date().getFullYear()
  const oldestYearToShow = Math.max(
    currentYear - MAX_YEARS_TO_SHOW + 1,
    HARDCODE_YEAR,
  )

  const yearPeriods = Array.from(
    { length: 4 },
    (_, i) => periodCalendar[i] || [],
  )

  const baseVariables = {
    iLocationGroupIds: [variables.bbbConstants?.COMP_GROUP_ID],
    iMetricCodes: ['total_sales'],
  }
  const skip = !variables.date || !variables.bbbConstants

  const skipYear0 = shouldSkipYear(yearPeriods[0], skip)
  const { data: currentData0, loading: currentLoading0 } = useQuery<IDataType>(
    currentQueries[0],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[0][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[0].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear0,
    },
  )
  const { data: yoyData0, loading: yoyLoading0 } = useQuery<IDataType>(
    yoyQueries[0],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[0][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[0].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear0,
    },
  )

  const skipYear1 =
    shouldSkipYear(yearPeriods[1], skip) || !currentData0 || !yoyData0
  const { data: currentData1, loading: currentLoading1 } = useQuery<IDataType>(
    currentQueries[1],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[1][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[1].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear1,
    },
  )
  const { data: yoyData1, loading: yoyLoading1 } = useQuery<IDataType>(
    yoyQueries[1],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[1][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[1].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear1,
    },
  )

  const skipYear2 =
    shouldSkipYear(yearPeriods[2], skip) || !currentData1 || !yoyData1
  const { data: currentData2, loading: currentLoading2 } = useQuery<IDataType>(
    currentQueries[2],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[2][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[2].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear2,
    },
  )
  const { data: yoyData2, loading: yoyLoading2 } = useQuery<IDataType>(
    yoyQueries[2],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[2][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[2].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear2,
    },
  )

  const skipYear3 =
    shouldSkipYear(yearPeriods[3], skip) || !currentData2 || !yoyData2
  const { data: currentData3, loading: currentLoading3 } = useQuery<IDataType>(
    currentQueries[3],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[3][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[3].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear3,
    },
  )
  const { data: yoyData3, loading: yoyLoading3 } = useQuery<IDataType>(
    yoyQueries[3],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[3][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[3].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear3,
    },
  )

  const loading =
    currentLoading0 ||
    yoyLoading0 ||
    currentLoading1 ||
    yoyLoading1 ||
    currentLoading2 ||
    yoyLoading2 ||
    currentLoading3 ||
    yoyLoading3

  return {
    data: useMemo((): IApiDataType => {
      const periods = periodCalendar
        .flat()
        .filter((p) => p.year >= oldestYearToShow)

      if (periods.length === 0) return null

      const yearDataMap: Record<
        number,
        { current: IDataType | undefined; yoy: IDataType | undefined }
      > = {}
      const yearPeriodsData = [
        { current: currentData0, yoy: yoyData0, periods: yearPeriods[0] },
        { current: currentData1, yoy: yoyData1, periods: yearPeriods[1] },
        { current: currentData2, yoy: yoyData2, periods: yearPeriods[2] },
        { current: currentData3, yoy: yoyData3, periods: yearPeriods[3] },
      ]
      yearPeriodsData.forEach(({ current, yoy, periods }) => {
        const year = periods[0]?.year
        if (year && year > HARDCODE_YEAR) {
          yearDataMap[year] = { current, yoy }
        }
      })

      const initialData =
        oldestYearToShow <= HARDCODE_YEAR
          ? (HARDCODE_DATA as Record<string, Record<string, number | null>>)
          : ({} as Record<string, Record<string, number | null>>)

      const getValue = (
        period: typeof periods[number],
        yearData:
          | { current: IDataType | undefined; yoy: IDataType | undefined }
          | undefined,
      ) => {
        if (!yearData?.current || !yearData?.yoy) return null

        const currentNode =
          yearData.current.trendLocationGroupMetricValues.nodes.find(
            (n) =>
              n.startDate === period.dateRange.startDateStr &&
              n.endDate === period.dateRange.endDateStr,
          )
        const yoyNode = yearData.yoy.trendLocationGroupMetricValues.nodes.find(
          (n) =>
            n.startDate === period.dateRange.startDateStr &&
            n.endDate === period.dateRange.endDateStr,
        )

        if (!currentNode || !yoyNode) return null

        return (
          ((currentNode.metricData.total_sales.value -
            yoyNode.metricData.total_sales.value) *
            100) /
          yoyNode.metricData.total_sales.value
        )
      }

      const metricPeriods = periods.reduce((result, period) => {
        const periodId = period.id
        const year = period.year

        if (period.dateRange.endDate.isAfter(NOW)) return result

        if (!result[periodId]) {
          result[periodId] = initialData[periodId] || {}
        }

        if (year === HARDCODE_YEAR) return result

        const yearData = yearDataMap[year]
        result[periodId] = {
          ...result[periodId],
          [`compSalesPercent${year}`]: getValue(period, yearData),
        }

        return result
      }, {} as Record<string, Record<string, number | null>>)

      return Object.keys(metricPeriods)
        .map((period) => ({
          ...metricPeriods[period],
          period,
        }))
        .sort(
          (a, b) =>
            parseInt(a.period.replace(/P/, ''), 10) -
            parseInt(b.period.replace(/P/, ''), 10),
        )
    }, [
      currentData0,
      currentData1,
      currentData2,
      currentData3,
      yoyData0,
      yoyData1,
      yoyData2,
      yoyData3,
      periodCalendar,
      oldestYearToShow,
      yearPeriods,
    ]),
    loading,
  }
}

export default useBbbAllPeriodMetric
